"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_PUZZLE_FILE_BYTES = void 0;
exports.exportPuzzles = exportPuzzles;
exports.importPuzzles = importPuzzles;
const storage_1 = require("./storage");
const progress_1 = require("./progress");
exports.MAX_PUZZLE_FILE_BYTES = 150_000_000;
const raster = /^data:image\/(png|jpeg|webp|gif|avif|bmp);base64,/;
async function dataURL(blob) {
    try {
        const bytes = new Uint8Array(await blob.arrayBuffer());
        let binary = '';
        for (let i = 0; i < bytes.length; i += 32768)
            binary += String.fromCharCode(...bytes.subarray(i, i + 32768));
        return `data:${blob.type};base64,${btoa(binary)}`;
    }
    catch (error) {
        // Some browsers abort arrayBuffer reads of IndexedDB-backed Files.
        // FileReader takes a different read path and can still recover the data.
        if (typeof FileReader === 'undefined')
            throw error;
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Image read returned no data.'));
            reader.onerror = () => reject(reader.error ?? new Error('Could not read image.'));
            reader.onabort = () => reject(new Error('Image read was interrupted.'));
            reader.readAsDataURL(blob);
        });
    }
}
function decodeImage(value, maxBytes, original = false) {
    if (typeof value !== 'string' || value.length > maxBytes * 1.4 + 100 || !(original ? /^data:(?:image\/[a-zA-Z0-9.+-]+|application\/octet-stream);base64,/.test(value) : raster.test(value)))
        throw new Error('Invalid puzzle image.');
    const [header, content] = value.split(',');
    const bytes = Uint8Array.from(atob(content), char => char.charCodeAt(0));
    if (bytes.length > maxBytes)
        throw new Error('Puzzle image is too large.');
    return new Blob([bytes], { type: header.slice(5, header.indexOf(';')) });
}
async function exportPuzzles(pictures, size, preview, progress, recovery = {}) {
    const records = [];
    let estimated = 0;
    for (const picture of pictures) {
        const original = picture.original;
        estimated += (picture.blob.size + (original?.size ?? 0)) * 1.4 + 1000;
        if (estimated > exports.MAX_PUZZLE_FILE_BYTES)
            throw new Error('Collection is too large. Export puzzles individually.');
        let image;
        try {
            image = await dataURL(picture.blob);
        }
        catch {
            if (!recovery.recoverImage)
                throw new Error(`Could not read the picture for "${picture.name}". Try reloading and exporting again.`);
            try {
                image = await dataURL(await recovery.recoverImage(picture));
            }
            catch {
                throw new Error(`Could not read the picture for "${picture.name}". Try reloading and exporting again.`);
            }
        }
        let originalData;
        if (original) {
            try {
                originalData = await dataURL(original);
            }
            catch {
                throw new Error(`The original for "${picture.name}" could not be read. Re-add the original image before exporting; no incomplete backup was created.`);
            }
        }
        records.push({ id: picture.id, name: picture.name, image, original: originalData });
    }
    const savedProgress = {};
    for (const p of pictures)
        for (const grid of [3, 4, 5]) {
            const key = `${p.id}:${grid}`;
            if ((0, progress_1.restorable)(progress[key], grid, Date.now()))
                savedProgress[key] = progress[key];
        }
    return new Blob([JSON.stringify({ format: 'balloons-puzzles', version: 1, size, randomSize: recovery.randomSize === true, preview, pictures: records, progress: savedProgress })], { type: 'application/octet-stream' });
}
function importPuzzles(text) {
    if (text.length > exports.MAX_PUZZLE_FILE_BYTES)
        throw new Error('Puzzle file is too large.');
    const data = JSON.parse(text);
    if (data.format !== undefined && (data.format !== 'balloons-puzzles' || data.version !== 1))
        throw new Error('Unsupported puzzle file version.');
    if (![3, 4, 5].includes(data.size) || !Array.isArray(data.pictures) || !data.pictures.length || data.pictures.length > 20)
        throw new Error('Choose a puzzle export file containing 1 to 20 puzzles.');
    const progress = {};
    const pictures = data.pictures.map((p) => {
        if (!p || typeof p.name !== 'string' || !p.name.trim() || p.name.length > 200)
            throw new Error('Invalid puzzle name.');
        const id = (0, storage_1.createPictureId)();
        if (typeof p.id === 'string')
            for (const grid of [3, 4, 5]) {
                const state = data.progress?.[`${p.id}:${grid}`];
                // Keep the round ID: it is also the seed for matching jigsaw outlines.
                if ((0, progress_1.restorable)(state, grid, Date.now()))
                    progress[`${id}:${grid}`] = state;
            }
        return { id, name: p.name, blob: decodeImage(p.image, 3_000_000), original: p.original === undefined ? undefined : decodeImage(p.original, 30_000_000, true) };
    });
    return { pictures, size: data.size, randomSize: data.randomSize === true, preview: data.preview === true, progress };
}
//# sourceMappingURL=transfer.js.map